import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyOutlined, EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, Input, notification, Tooltip } from "antd";

import type { InputRef } from "antd";

interface CredentialFieldProps {
  label: string;
  value: string;
  maskedValue: string;
  showLabel: string;
  hideLabel: string;
  copyLabel: string;
}

export function CredentialField({
  label,
  value,
  maskedValue,
  showLabel,
  hideLabel,
  copyLabel,
}: CredentialFieldProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<InputRef>(null);
  const copyTimeout = useRef<ReturnType<typeof window.setTimeout>>(undefined);
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => () => window.clearTimeout(copyTimeout.current), []);

  useLayoutEffect(() => {
    const input = inputRef.current?.input;
    if (input) {
      input.setSelectionRange(0, 0);
      input.scrollLeft = 0;
    }
  }, [visible, value, maskedValue]);

  const copy = async () => {
    window.clearTimeout(copyTimeout.current);
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      copyTimeout.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
      notification.error({ message: t("externalServices.copyError") });
    }
  };

  return (
    <>
      <label htmlFor={inputId}>{label}</label>
      <Input
        id={inputId}
        ref={inputRef}
        value={visible ? value : maskedValue}
        readOnly
        dir="ltr"
        autoComplete="off"
        spellCheck={false}
        suffix={
          <>
            <Button
              type="text"
              size="small"
              aria-label={visible ? hideLabel : showLabel}
              aria-pressed={visible}
              aria-controls={inputId}
              icon={visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                setVisible((current) => !current);
              }}
            />
            <Tooltip title={t("externalServices.copied")} open={copied}>
              <Button
                type="text"
                size="small"
                aria-label={copyLabel}
                icon={<CopyOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  void copy();
                }}
              />
            </Tooltip>
          </>
        }
      />
    </>
  );
}
